import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CATEGORY_LABEL } from "@/mocks/products";
import { useLocalized } from "@/lib/localize";
import { asset } from "@/lib/asset";

interface CategoryEntry {
  id: string;
  image: string;
}

const categoryData: CategoryEntry[] = [
  { id: "Dining Tables", image: asset("/editorial/categories/dining-tables.jpg") },
  { id: "Coffee Tables", image: asset("/editorial/categories/coffee-tables.jpg") },
  { id: "Console Tables", image: asset("/editorial/categories/console-tables.jpg") },
  { id: "Chairs", image: asset("/editorial/categories/chairs.jpg") },
  { id: "Sofas", image: asset("/editorial/categories/sofa.jpg") },
  { id: "TV Cabinets", image: asset("/editorial/categories/tv-cabinets.jpg") },
];

const CategoriesSection = () => {
  const { t } = useTranslation();
  const loc = useLocalized();

  return (
    <section className="py-14 md:py-24 bg-stone-50">
      <div className="max-w-screen-xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-amber-700 text-xs font-semibold tracking-widest uppercase mb-3">
            {t("home.categories.kicker")}
          </p>
          <h2 className="text-3xl md:text-5xl font-bold text-stone-900 mb-4">
            {t("home.categories.heading")}
          </h2>
          <p className="text-stone-500 text-base max-w-xl mx-auto">
            {t("home.categories.description")}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {categoryData.map((cat, i) => {
            const label = loc(CATEGORY_LABEL[cat.id]);
            const isFeatured = i === 0;
            return (
              <Link
                key={cat.id}
                to={`/products?category=${encodeURIComponent(cat.id)}`}
                className={`group relative overflow-hidden rounded-2xl cursor-pointer bg-stone-900 ${
                  isFeatured ? "col-span-2 md:col-span-2 md:row-span-2" : ""
                }`}
                style={{
                  minHeight: isFeatured
                    ? "clamp(200px, 50vw, 420px)"
                    : "clamp(140px, 32vw, 220px)",
                }}
              >
                <img
                  src={cat.image}
                  alt={label}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                {/* Frame inset on hover */}
                <div className="absolute inset-4 border border-white/0 group-hover:border-amber-400/40 rounded-xl transition-all duration-300" />

                <div className="absolute bottom-0 left-0 right-0 p-5 flex flex-col gap-1">
                  <p className="text-amber-300/80 text-[10px] font-semibold tracking-widest uppercase">
                    Forwaro · {label}
                  </p>
                  <p
                    className="text-white font-bold leading-tight"
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: isFeatured ? "2.25rem" : "1.5rem",
                    }}
                  >
                    {label}
                  </p>
                </div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <span className="bg-white/15 backdrop-blur-sm border border-white/30 text-white text-xs font-semibold px-5 py-2 rounded-full">
                    {t("common.details")}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="text-center mt-10 flex flex-wrap justify-center gap-4">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 border border-stone-300 hover:border-stone-500 text-stone-700 hover:text-stone-900 text-sm font-semibold px-8 py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            {t("cta.viewAll")}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CategoriesSection;
